import React from "react";
import classNames from "classnames";

const Button = React.forwardRef(
  (
    {
      children,
      variant = "default",
      size = "md",
      className,
      icon: Icon,
      iconPosition = "right",
      iconClassName = "",
      isLoading = false,
      loadingText = "Loading...",
      disabled = false,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "flex items-center justify-center rounded-xl font-medium transition-colors text-caption flex-grow-0 flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed";

    const variants = {
      default: "bg-secondary/95 text-background/90 hover:bg-secondary/80",
      outline:
        "bg-transparent border border-[1.6px] border-secondary/30 hover:bg-secondary/20 text-secondary/70",
      ghost: "bg-transparent hover:bg-secondary/20 text-secondary/90",
      destructive: "bg-rose-500 text-white hover:bg-rose-600",
      destructiveOutline: "bg-transparent border border-[1.6px] border-rose-500/60 text-rose-500 hover:bg-rose-600/10",
    };

    const sizes = {
      xs: "px-2 py-1 text-xs !rounded-md gap-1",
      sm: "px-3 py-2 text-sm !rounded-[10px] gap-2",
      md: "px-4 py-2 text-lg gap-2",
      lg: "px-5 py-3 text-xl gap-2",
    };

    // Icon sizes based on button size
    const iconSizes = {
      xs: "w-3 h-3",
      sm: "w-4 h-4",
      md: "w-5 h-5",
      lg: "w-6 h-6",
    };

    const buttonClasses = classNames(
      baseStyles,
      variants[variant],
      sizes[size],
      className
    );

    const iconClasses = classNames(
      iconSizes[size],
      iconClassName
    );

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isLoading || disabled}
        {...props}
      >
        {Icon && iconPosition === "left" && !isLoading && (
          <Icon className={iconClasses} />
        )}

        {isLoading ? (
          <>
            <span>{loadingText}</span>
            <svg
              className="animate-spin h-5 w-5 ml-1 text-inherit"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V2a10 10 0 0 0 0 20v-2a8 8 0 0 1-8-8z"
              />
            </svg>
          </>
        ) : (
          children
        )}

        {Icon && iconPosition === "right" && !isLoading && (
          <Icon className={iconClasses} />
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
